import React from 'react';
import Icon from '../../../components/AppIcon';
import { useLanguage } from '../../../contexts/LanguageContext';

const AttendanceTrackingTable = ({
  rows,
  loading,
  searchQuery,
  onSearchChange,
  filterStatus,
  onFilterStatusChange,
  filterSite,
  onFilterSiteChange,
  sites,
}) => {
  const { t } = useLanguage();
  const filtered = rows?.filter((row) => {
    const matchSearch =
      !searchQuery ||
      row?.worker?.full_name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      row?.talk?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase());

    const matchStatus =
      filterStatus === 'all' ||
      (filterStatus === 'confirmed' && row?.confirmed) ||
      (filterStatus === 'pending' && !row?.confirmed);

    const matchSite =
      !filterSite ||
      row?.talk?.site?.id === filterSite;

    return matchSearch && matchStatus && matchSite;
  });

  const formatDate = (ts) => {
    if (!ts) return '—';
    return new Date(ts)?.toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      {/* Header */}
      <div className="px-4 sm:px-6 py-4 border-b border-border">
        <div className="flex flex-col gap-3">
          <div>
            <h2 className="text-base font-semibold text-foreground">{t('toolboxAttendanceTracking')}</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {filtered?.length} {t('ramsRecord')}{filtered?.length !== 1 ? 's' : ''}
            </p>
          </div>
          {/* Filters stack vertically on mobile */}
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Icon name="Search" size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder={t('toolboxSearchPlaceholder')}
                value={searchQuery}
                onChange={(e) => onSearchChange(e?.target?.value)}
                className="w-full pl-8 pr-3 py-2.5 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent min-h-[44px]"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => onFilterStatusChange(e?.target?.value)}
              className="px-3 py-2.5 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 min-h-[44px]"
            >
              <option value="all">{t('ramsAllStatus')}</option>
              <option value="confirmed">{t('toolboxConfirmed')}</option>
              <option value="pending">{t('toolboxPending')}</option>
            </select>
            <select
              value={filterSite}
              onChange={(e) => onFilterSiteChange(e?.target?.value)}
              className="px-3 py-2.5 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 min-h-[44px]"
            >
              <option value="">{t('ramsAllSites')}</option>
              {sites?.map((s) => (
                <option key={s?.id} value={s?.id}>{s?.site_name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent" />
        </div>
      ) : filtered?.length === 0 ? (
        <div className="text-center py-12">
          <Icon name="ClipboardList" size={32} className="text-muted-foreground mx-auto mb-3" />
          <p className="text-sm font-medium text-foreground">{t('ramsNoRecords')}</p>
          <p className="text-xs text-muted-foreground mt-1">{t('toolboxNoRecordsDesc')}</p>
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/40 border-b border-border">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('ramsColWorker')}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('toolboxColTalk')}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('ramsColSite')}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('ramsColStatus')}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('toolboxColConfirmedAt')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered?.map((row) => (
                  <tr key={row?.id} className="hover:bg-muted/20 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-accent/10 flex items-center justify-center shrink-0">
                          <span className="text-xs font-semibold text-accent">
                            {row?.worker?.full_name?.charAt(0)?.toUpperCase()}
                          </span>
                        </div>
                        <span className="font-medium text-foreground">{row?.worker?.full_name || '—'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-foreground">{row?.talk?.title || '—'}</td>
                    <td className="px-4 py-3 text-muted-foreground">{row?.talk?.site?.site_name || '—'}</td>
                    <td className="px-4 py-3">
                      {row?.confirmed ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-semibold bg-success/10 text-success">
                          <Icon name="CheckCircle" size={12} />
                          {t('toolboxConfirmed')}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-semibold bg-destructive/10 text-destructive">
                          <Icon name="Clock" size={12} />
                          {t('toolboxPending')}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground text-xs">
                      {formatDate(row?.confirmed_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile card-stack */}
          <div className="md:hidden divide-y divide-border">
            {filtered?.map((row) => (
              <div key={row?.id} className="p-4 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center shrink-0">
                      <span className="text-xs font-semibold text-accent">
                        {row?.worker?.full_name?.charAt(0)?.toUpperCase()}
                      </span>
                    </div>
                    <span className="font-semibold text-sm text-foreground truncate">{row?.worker?.full_name || '—'}</span>
                  </div>
                  {row?.confirmed ? (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-semibold bg-success/10 text-success shrink-0">
                      <Icon name="CheckCircle" size={11} />
                      {t('toolboxConfirmed')}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-semibold bg-destructive/10 text-destructive shrink-0">
                      <Icon name="Clock" size={11} />
                      {t('toolboxPending')}
                    </span>
                  )}
                </div>
                <p className="text-sm text-foreground font-medium">{row?.talk?.title || '—'}</p>
                <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                  {row?.talk?.site?.site_name && (
                    <span className="flex items-center gap-1">
                      <Icon name="MapPin" size={11} />
                      {row?.talk?.site?.site_name}
                    </span>
                  )}
                  {row?.confirmed_at && (
                    <span className="flex items-center gap-1">
                      <Icon name="Clock" size={11} />
                      {formatDate(row?.confirmed_at)}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default AttendanceTrackingTable;
